import { sapiBaseUrl, type SiFlightSession } from "./sayintentions";
import type { Mission } from "./types";

/** Live field weather from SAPI `getWX`. Never invent METAR / ATIS / TAF. */
export type FieldWx = {
  icao: string;
  metar: string;
  atis: string;
  atisCpdlc: string;
  taf: string;
  activeRunway: string;
  windDirection: number | null;
  windSpeed: number | null;
};

export type WxChipRole = "DEP" | "ARR" | "ALTN" | "FLD";

const ICAO_RE = /^[A-Z0-9]{3,4}$/;
const METAR_WIND_RE = /\b(?:\d{3}|VRB)\d{2,3}(?:G\d{2,3})?KT\b/;
const ATIS_SUMMARY_MAX = 180;
const DEFAULT_ICAO_LIMIT = 8;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asText(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value).trim();
  }
  return "";
}

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function normalizeIcao(value: unknown): string {
  return asText(value).toUpperCase();
}

export function isWxIcao(value: string): boolean {
  return ICAO_RE.test(value);
}

export function uniqueIcaos(values: Array<string | undefined | null>, limit = DEFAULT_ICAO_LIMIT): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of values) {
    const icao = normalizeIcao(raw);
    if (!isWxIcao(icao) || seen.has(icao)) continue;
    seen.add(icao);
    out.push(icao);
    if (out.length >= limit) break;
  }
  return out;
}

export function wxNoKeyReason(): string {
  return "Field weather needs a Flight tracker API key (flightJSON or Settings override). This desk does not invent METAR, ATIS, or TAF.";
}

export function wxOfflineCopy(detail?: string): string {
  const extra = detail?.trim();
  return extra
    ? `${extra} No invented weather.`
    : "Field weather is unreachable. No invented METAR, ATIS, or TAF.";
}

export function hasFieldWx(field: FieldWx | undefined | null): boolean {
  if (!field) return false;
  return Boolean(field.metar || field.atis || field.atisCpdlc || field.taf);
}

export function emptyFieldWx(icao: string): FieldWx {
  return {
    icao: normalizeIcao(icao),
    metar: "",
    atis: "",
    atisCpdlc: "",
    taf: "",
    activeRunway: "",
    windDirection: null,
    windSpeed: null,
  };
}

function fieldFromRecord(rec: Record<string, unknown>, fallbackIcao = ""): FieldWx {
  const icao =
    normalizeIcao(rec.airport) ||
    normalizeIcao(rec.icao) ||
    normalizeIcao(rec.ident) ||
    normalizeIcao(fallbackIcao);
  return {
    icao,
    metar: asText(rec.metar),
    atis: asText(rec.atis),
    atisCpdlc: asText(rec.atis_cpdlc) || asText(rec.atisCpdlc),
    taf: asText(rec.taf),
    activeRunway: asText(rec.active_runway) || asText(rec.activeRunway) || asText(rec.runway),
    windDirection: asNumber(rec.wind_direction) ?? asNumber(rec.windDirection),
    windSpeed: asNumber(rec.wind_speed) ?? asNumber(rec.windSpeed),
  };
}

/**
 * Accepts SAPI `{ airports: [...] }`, a single airport object, or the older
 * portal `{ metar, taf, atis }` shape. Returns only rows that actually have
 * weather text or wind — never a fabricated METAR.
 */
export function parseGetWx(raw: unknown, requestedIcaos: string[] = []): FieldWx[] {
  if (raw == null) return [];
  const root = asRecord(raw);
  if (root) {
    const err = asText(root.error) || asText(root.message);
    const status = asText(root.status);
    if (err && status && status.toUpperCase() !== "OK") return [];
    if (err && !root.airports && !root.metar && !root.atis && !root.taf) return [];

    const list = Array.isArray(root.airports)
      ? root.airports
      : Array.isArray(root.data)
        ? root.data
        : null;
    if (list) {
      return list.flatMap((row) => {
        const rec = asRecord(row);
        if (!rec) return [];
        const field = fieldFromRecord(rec);
        return isWxIcao(field.icao) && hasFieldWx(field) ? [field] : [];
      });
    }

    if (asText(root.metar) || asText(root.atis) || asText(root.taf) || asText(root.atis_cpdlc)) {
      const field = fieldFromRecord(root, requestedIcaos[0] ?? "");
      return isWxIcao(field.icao) && hasFieldWx(field) ? [field] : [];
    }
  }

  if (Array.isArray(raw)) {
    return raw.flatMap((row) => {
      const rec = asRecord(row);
      if (!rec) return [];
      const field = fieldFromRecord(rec);
      return isWxIcao(field.icao) && hasFieldWx(field) ? [field] : [];
    });
  }

  return [];
}

export function parseGetWxError(raw: unknown, httpStatus?: number): string {
  const root = asRecord(raw);
  const err = root ? asText(root.error) || asText(root.message) : "";
  if (err) return err;
  if (httpStatus === 401 || httpStatus === 403) {
    return "Weather rejected the tracker key.";
  }
  if (httpStatus && httpStatus >= 400) {
    return `Weather HTTP ${httpStatus}`;
  }
  return "";
}

export function buildGetWxUrl(
  session: SiFlightSession | null,
  apiKey: string,
  icaos: string[],
): string {
  const params = new URLSearchParams();
  params.set("api_key", apiKey.trim());
  params.set("icao", uniqueIcaos(icaos).join(","));
  return `${sapiBaseUrl(session)}/getWX?${params.toString()}`;
}

export function formatWxWind(field: FieldWx): string {
  if (field.windDirection != null && field.windSpeed != null) {
    const dir = String(Math.round(field.windDirection) % 360).padStart(3, "0");
    const spd = String(Math.round(field.windSpeed)).padStart(2, "0");
    return `${dir}/${spd}`;
  }
  const fromMetar = field.metar.match(METAR_WIND_RE);
  return fromMetar?.[0] ?? "";
}

export function summarizeAtis(field: FieldWx): string {
  const raw = field.atis || field.atisCpdlc;
  if (!raw) return "";
  const sentence = raw.split(/(?<=[.!?])\s+/)[0] ?? raw;
  const clipped = sentence.length > ATIS_SUMMARY_MAX ? `${sentence.slice(0, ATIS_SUMMARY_MAX - 1).trimEnd()}…` : sentence;
  return clipped.trim();
}

export function wxChipLabel(field: FieldWx | undefined, role: WxChipRole): string {
  if (!field || !hasFieldWx(field)) return `${role} —`;
  const wind = formatWxWind(field);
  return wind ? `${role} ${field.icao} ${wind}` : `${role} ${field.icao}`;
}

export function collectWxIcaos(input: {
  session?: Pick<SiFlightSession, "originIcao" | "destIcao" | "currentAirport"> | null;
  missions?: Array<Pick<Mission, "originIcao" | "destIcao" | "status">>;
  selected?: Array<string | undefined | null>;
  extras?: Array<string | undefined | null>;
  limit?: number;
}): string[] {
  const fromMissions = (input.missions ?? [])
    .filter((m) => m.status === "pending" || m.status === "in-progress")
    .flatMap((m) => [m.originIcao, m.destIcao]);
  return uniqueIcaos(
    [
      input.session?.originIcao,
      input.session?.destIcao,
      input.session?.currentAirport,
      ...fromMissions,
      ...(input.selected ?? []),
      ...(input.extras ?? []),
    ],
    input.limit ?? DEFAULT_ICAO_LIMIT,
  );
}

export function fieldsByIcao(fields: FieldWx[]): Record<string, FieldWx> {
  const out: Record<string, FieldWx> = {};
  for (const field of fields) {
    if (field.icao) out[field.icao] = field;
  }
  return out;
}
